import React, { useState, useCallback, useEffect } from 'react';
import { useSubscription } from '../hooks/useSubscription';
import API_BASE_URL from '../config';
import '../styles/components/subscriptionTab.css';

interface SubscriptionTabProps {
  onViewPlans?: () => void;
}

const SubscriptionTab: React.FC<SubscriptionTabProps> = ({ onViewPlans }) => {
  const {
    subscription,
    tierInfo,
    usage,
    loading,
    error,
    getUsagePercentage,
    formatLimit,
    formatStorage,
    getPriceDisplay,
    canUseFeature,
    refresh,
  } = useSubscription();

  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState<string | null>(null);

  // When user returns from Stripe portal (or tab regains focus), refresh
  // subscription data so "Renews on" / "Ends on" stays accurate.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refresh();
      }
    };

    const handleFocus = () => {
      refresh();
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refresh]);

  const handleManageBilling = useCallback(async () => {
    if (!subscription || subscription.tier === 'free' || billingLoading) return;

    try {
      setBillingLoading(true);
      setBillingError(null);

      const token = localStorage.getItem('auth_token');
      if (!token) {
        setBillingError('You need to be signed in to manage billing.');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/payment/portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.url) {
        console.error('Failed to create billing portal session', data);
        setBillingError('Unable to open billing portal right now. Please try again later.');
        return;
      }

      window.location.href = data.url;
    } catch (e) {
      console.error('Error opening billing portal:', e);
      setBillingError('Unable to open billing portal right now. Please try again later.');
    } finally {
      setBillingLoading(false);
    }
  }, [subscription, billingLoading]);

  if (loading) {
    return (
      <div className="subscription-tab">
        <div className="loading-message">Loading subscription...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="subscription-tab">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (!subscription || !tierInfo || !usage) {
    return (
      <div className="subscription-tab">
        <div className="error-message">No subscription data available</div>
      </div>
    );
  }

  const itemsPercentage = getUsagePercentage('items');
  const scansPercentage = getUsagePercentage('ai_scans_per_month');
  const storagePercentage = getUsagePercentage('storage_mb');

  const isFreeTier = subscription.tier === 'free';
  const periodEnd = subscription.current_period_end;
  const hasPeriodEnd = !!periodEnd;
  let renewalText: string | null = null;

  if (!isFreeTier && hasPeriodEnd && periodEnd) {
    const endDate = new Date(periodEnd).toLocaleDateString();
    if (subscription.status === 'canceled') {
      renewalText = `Ended on ${endDate}`;
    } else if (subscription.cancel_at_period_end) {
      renewalText = `Ends on ${endDate} - no renewal scheduled. Use Manage billing to renew`;
    } else {
      renewalText = `Renews on ${endDate}`;
    }
  }

  return (
    <div className="subscription-tab">
      {/* Current Plan */}
      <div className="subscription-section">
        <h2>Current Plan</h2>
        <div className="current-plan-card">
          <div className="plan-header">
            <h3>{tierInfo.display_name}</h3>
            {!isFreeTier && (
              <div className="plan-price">{getPriceDisplay()}</div>
            )}
          </div>
          <p className="plan-description">{tierInfo.description}</p>

          {renewalText && (
            <div className="plan-renewal-info">{renewalText}</div>
          )}

          <div className="plan-actions">
            {subscription.tier !== 'business' && (
              <button
                className="upgrade-button"
                onClick={onViewPlans}
              >
                Upgrade to unlock more features
              </button>
            )}

            {/* Show appropriate billing management based on payment source */}
            {!isFreeTier && subscription.payment_source === 'stripe' && (
              <button
                className="manage-billing-button"
                onClick={handleManageBilling}
                disabled={billingLoading}
              >
                {billingLoading ? 'Opening billing…' : 'Manage billing'}
              </button>
            )}
            {!isFreeTier && subscription.payment_source === 'ios_iap' && (
              <button
                className="manage-billing-button"
                onClick={() => {
                  // Deep link to iOS subscription settings
                  window.location.href = 'https://apps.apple.com/account/subscriptions';
                }}
              >
                Manage in App Store
              </button>
            )}
          </div>
        </div>
      </div>

      {billingError && (
        <div className="subscription-section">
          <div className="error-message">{billingError}</div>
        </div>
      )}

      {/* Usage Stats */}
      <div className="subscription-section">
        <h2>Usage</h2>
        
        {/* Items */}
        <div className="usage-item">
          <div className="usage-header">
            <span className="usage-label">Items</span>
            <span className="usage-count">
              {usage.items.used.toLocaleString()} / {formatLimit(usage.items.limit)}
            </span>
          </div>
          <div className="usage-bar">
            <div 
              className={`usage-fill ${itemsPercentage >= 90 ? 'usage-warning' : ''}`}
              style={{ width: `${Math.min(100, itemsPercentage)}%` }}
            />
          </div>
          {subscription.bonus_items > 0 && (
            <div className="bonus-info">
              +{subscription.bonus_items} bonus items from referrals
            </div>
          )}
        </div>

        {/* AI Scans */}
        <div className="usage-item">
          <div className="usage-header">
            <span className="usage-label">AI Scans (this month)</span>
            <span className="usage-count">
              {usage.ai_scans_per_month.used} / {formatLimit(usage.ai_scans_per_month.limit)}
            </span>
          </div>
          <div className="usage-bar">
            <div 
              className={`usage-fill ${scansPercentage >= 90 ? 'usage-warning' : ''}`}
              style={{ width: `${Math.min(100, scansPercentage)}%` }}
            />
          </div>
          {subscription.bonus_ai_scans > 0 && (
            <div className="bonus-info">
              +{subscription.bonus_ai_scans} bonus scans from referrals
            </div>
          )}
        </div>

        {/* Storage */}
        <div className="usage-item">
          <div className="usage-header">
            <span className="usage-label">Storage</span>
            <span className="usage-count">
              {formatStorage(usage.storage_mb.used)} / {formatStorage(usage.storage_mb.limit)}
            </span>
          </div>
          <div className="usage-bar">
            <div 
              className={`usage-fill ${storagePercentage >= 90 ? 'usage-warning' : ''}`}
              style={{ width: `${Math.min(100, storagePercentage)}%` }}
            />
          </div>
          {subscription.bonus_storage_mb > 0 && (
            <div className="bonus-info">
              +{formatStorage(subscription.bonus_storage_mb)} bonus storage from referrals
            </div>
          )}
        </div>
      </div>

      {/* Features */}
      <div className="subscription-section">
        <h2>Features</h2>
        <div className="features-list">
          <div className={`feature-item ${canUseFeature('basic_inventory') ? 'feature-enabled' : 'feature-disabled'}`}>
            <span className="feature-icon">{canUseFeature('basic_inventory') ? '✓' : '✗'}</span>
            <span className="feature-name">Basic Inventory Management</span>
          </div>
          <div className={`feature-item ${canUseFeature('guest_sharing') ? 'feature-enabled' : 'feature-disabled'}`}>
            <span className="feature-icon">{canUseFeature('guest_sharing') ? '✓' : '✗'}</span>
            <span className="feature-name">Guest Sharing</span>
          </div>
          <div className={`feature-item ${canUseFeature('insurance_docs') ? 'feature-enabled' : 'feature-disabled'}`}>
            <span className="feature-icon">{canUseFeature('insurance_docs') ? '✓' : '✗'}</span>
            <span className="feature-name">Insurance Documentation</span>
          </div>
          <div className={`feature-item ${canUseFeature('group_sharing') ? 'feature-enabled' : 'feature-disabled'}`}>
            <span className="feature-icon">{canUseFeature('group_sharing') ? '✓' : '✗'}</span>
            <span className="feature-name">Group Sharing</span>
          </div>
          <div className={`feature-item ${canUseFeature('advanced_export') ? 'feature-enabled' : 'feature-disabled'}`}>
            <span className="feature-icon">{canUseFeature('advanced_export') ? '✓' : '✗'}</span>
            <span className="feature-name">Advanced Export</span>
          </div>
        </div>
      </div>

      {/* Stats */}
      {usage.total_ai_scans > 0 && (
        <div className="subscription-section">
          <h2>Statistics</h2>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-value">{usage.total_ai_scans.toLocaleString()}</div>
              <div className="stat-label">Total AI Scans</div>
            </div>
            {usage.last_ai_scan_at && (
              <div className="stat-item">
                <div className="stat-value">
                  {new Date(usage.last_ai_scan_at).toLocaleDateString()}
                </div>
                <div className="stat-label">Last AI Scan</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Legal Links - Required by Apple App Store */}
      <div className="subscription-section legal-links">
        <div className="legal-links-container">
          <a href="/help/terms" target="_blank" rel="noopener noreferrer" className="legal-link">
            Terms of Use
          </a>
          <span className="legal-separator">•</span>
          <a href="/help/privacy" target="_blank" rel="noopener noreferrer" className="legal-link">
            Privacy Policy
          </a>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionTab;
